import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import { useIsAuthenticated } from '../../state_management_and_stores/auth.store';

/**
 * Transaction History hook — powers the Profile → "Transaction History" tab.
 *
 * Hits `GET /payment-ops/my/transactions`. Returns the unified financial
 * feed (Stripe payments + refunds, cash receipts, manual ledger entries,
 * and pending pay-later registrations) the user expects to see for the
 * full money story on their account.
 *
 * Distinct from `useMyRegistrations` (which is per-entry) and
 * `useOutstandingBalance` (which is a roll-up): this is the per-event-of-
 * money view.
 */

// Mirror of TransactionHistoryService types on the backend. Keep these in
// lockstep with `transaction-history.service.ts` — when fields change there
// they need updating here too. There's no generated client because the
// endpoint isn't auto-generated from an entity (it's a synthetic feed).
export type TransactionProvider = 'stripe' | 'highcall';
export type TransactionKind =
  | 'payment'
  | 'refund'
  | 'adjustment'
  | 'pending'
  /** Registration an admin waived to $0 — nothing owed, nothing captured. */
  | 'free'
  | 'failed';

export interface CoveredRegistration {
  registration_id: string;
  entrant_name: string | null;
  organization_name: string | null;
  rodeo_name: string | null;
  event_name: string | null;
  amount: number;
  entry_fee: number | null;
  transaction_fee: number | null;
  non_member_fee: number;
  /** Split tender: amount of this registration settled with org credit. */
  credit_applied: number;
}

export interface TransactionItem {
  id: string;
  kind: TransactionKind;
  occurred_at: string;
  amount: number;
  currency: string;
  status: string;
  provider: TransactionProvider;
  reference: string | null;
  type_label: string;
  reason: string | null;
  organization_name: string | null;
  payment_method: {
    type: string | null;
    brand: string | null;
    last4: string | null;
    bank_name: string | null;
  } | null;
  is_bulk: boolean;
  covered_registrations: CoveredRegistration[];
  registration_id: string | null;
  selected_payment_method: string | null;
  created_by_display_name: string | null;
  claimed_payer_display_name: string | null;
  claimed_payer_user_id: string | null;
  /** For kind='pending' team-roping primaries: the row's partner_status.
   *  'needs_partner' = previous partner declined; 'pending' = invited
   *  partner hasn't yet responded. Drives the Transactions tab's
   *  awaiting-partner labelling — without this the row read as a queued
   *  Stripe charge that hadn't actually been authorized. */
  partner_status: string | null;
  /** For kind='adjustment': ISO timestamp the manual ledger entry was settled
   *  by a Stripe payment, or null if still open. Drives the admin delete
   *  affordance (settled entries can't be deleted). Null on other rows. */
  settled_at: string | null;
  /** For kind='adjustment' charges: true when it blocks new event entries for
   *  its org until paid (admin-flagged, e.g. a fine). Drives the "Blocks
   *  entry" badge. False on other rows. */
  blocks_event_entry: boolean;
}

export interface TransactionFeed {
  items: TransactionItem[];
  included_failed: boolean;
}

interface UseTransactionHistoryOptions {
  /** Include failed Stripe attempts (declined, NSF, expired PI). Defaults
   *  to false — most users find these noisy. The Transaction History tab
   *  surfaces a toggle that flips this on. */
  includeFailed?: boolean;
  /**
   * Admin override: fetch the feed for THIS user_profiles.id instead of the
   * authenticated user. When set, hits the admin-gated
   * `/payment-ops/admin/users/:userId/transactions` endpoint so the admin
   * User-Details → Financials tab can render the exact same display the
   * contestant sees. Omit for the self view.
   */
  userId?: string;
}

export const transactionHistoryKeys = {
  all: ['transaction-history'] as const,
  feed: (opts?: UseTransactionHistoryOptions) =>
    opts
      ? ([...transactionHistoryKeys.all, 'feed', opts] as const)
      : ([...transactionHistoryKeys.all, 'feed'] as const),
};

export function useTransactionHistory(opts?: UseTransactionHistoryOptions) {
  const isAuthenticated = useIsAuthenticated();
  return useQuery<TransactionFeed>({
    queryKey: transactionHistoryKeys.feed(opts),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.includeFailed) params.set('include_failed', 'true');
      const qs = params.toString();
      const base = opts?.userId
        ? `/payment-ops/admin/users/${opts.userId}/transactions`
        : '/payment-ops/my/transactions';
      return httpClient.get<TransactionFeed>(`${base}${qs ? `?${qs}` : ''}`);
    },
    enabled: isAuthenticated,
    // Match other financial endpoints — money state is high-trust, refresh
    // when the user returns to the tab.
    staleTime: 30 * 1000,
  });
}
